// Canonical event-type list (EN + KA). Single source of truth shared by the
// create/edit form and the guest invitation page so the type a host picks is
// always shown in the viewer's language.

export type EventTypeOption = {
  en: string
  ka: string
  // Example shown as the title-field placeholder once this type is picked,
  // e.g. birthday → "მაგ., კირას დაბადების დღის წვეულება".
  exampleEn: string
  exampleKa: string
}

export const EVENT_TYPES: EventTypeOption[] = [
  { en: 'Birthday Party', ka: 'დაბადების დღე', exampleEn: "e.g., Kira's Birthday Party", exampleKa: 'მაგ., კირას დაბადების დღის წვეულება' },
  { en: 'Wedding', ka: 'ქორწილი', exampleEn: "e.g., Ana & Dato's Wedding", exampleKa: 'მაგ., ანასა და დათოს ქორწილი' },
  { en: 'Baby Shower', ka: 'ბეიბი შაუერი', exampleEn: "e.g., Mariam's Baby Shower", exampleKa: 'მაგ., მარიამის ბეიბი შაუერი' },
  { en: 'Corporate Event', ka: 'კორპორატიული ღონისძიება', exampleEn: "e.g., Company New Year Gala", exampleKa: 'მაგ., კომპანიის საახალწლო საღამო' },
  { en: 'Dinner Party', ka: 'სადილი', exampleEn: 'e.g., Dinner with Friends', exampleKa: 'მაგ., სადილი მეგობრებთან ერთად' },
  { en: 'Anniversary', ka: 'იუბილე', exampleEn: 'e.g., 10th Wedding Anniversary', exampleKa: 'მაგ., ქორწინების 10 წლის იუბილე' },
  { en: 'Graduation', ka: 'გამოსაშვები', exampleEn: "e.g., Nika's Graduation Party", exampleKa: 'მაგ., ნიკას გამოსაშვები საღამო' },
  { en: 'Holiday Celebration', ka: 'დღესასწაული', exampleEn: "e.g., New Year's Eve Party", exampleKa: 'მაგ., საახალწლო წვეულება' },
  { en: 'Charity Event', ka: 'საქველმოქმედო ღონისძიება', exampleEn: 'e.g., Charity Gala for the Shelter', exampleKa: 'მაგ., საქველმოქმედო საღამო თავშესაფრისთვის' },
  { en: 'Trip', ka: 'მოგზაურობა', exampleEn: 'e.g., Weekend Trip to Kazbegi', exampleKa: 'მაგ., შაბათ-კვირის მოგზაურობა ყაზბეგში' },
  { en: 'Surprise Gift', ka: 'გახარება', exampleEn: 'e.g., A Surprise for Nino', exampleKa: 'მაგ., სიურპრიზი ნინოსთვის' },
  { en: 'Other', ka: 'სხვა', exampleEn: 'e.g., Housewarming Party', exampleKa: 'მაგ., ბინის დალოცვის წვეულება' },
]

// Title-field placeholder example for a stored event-type value, in the
// requested language. Empty string when no type is selected yet.
export const titleExampleFor = (value?: string, lang: string = 'en'): string => {
  if (!value) return ''
  const match = EVENT_TYPES.find((t) => t.en === value || t.ka === value)
  if (!match) return ''
  return lang === 'ka' ? match.exampleKa : match.exampleEn
}

// Map a stored event-type value (stored as the English label) to the requested
// language. Falls back to the raw value for custom/legacy types.
export const localizeEventType = (value?: string, lang: string = 'en'): string => {
  if (!value) return ''
  const match = EVENT_TYPES.find((t) => t.en === value || t.ka === value)
  if (!match) return value
  return lang === 'ka' ? match.ka : match.en
}

// Event types where a gift registry does not belong. A company gala or a
// fundraiser still needs a guest list and RSVPs, but asking attendees to chip
// in for the host's wishlist is wrong — so the wizard defaults the gifts
// section off for these and says why, rather than hiding the control.
// The host can still switch it back on; this only moves the default.
const GIFTLESS_TYPES = new Set([
  'Corporate Event',
  'კორპორატიული ღონისძიება',
  'Charity Event',
  'საქველმოქმედო ღონისძიება',
])

/** Does a gift registry make sense for this event type? */
export const eventTypeWantsGifts = (value?: string): boolean =>
  !value || !GIFTLESS_TYPES.has(value)
